import { Input, Select, SelectItem, Switch, Textarea } from '@nextui-org/react';
import { Icon } from '@iconify/react';
import { CtyButton } from '@circuitry-ai/doc-ui';
import {
  BUSINESS_TOOL_EXECUTION_MODES,
  BUSINESS_TOOL_PARAMETER_TYPES,
  createEmptyParameter,
  type BusinessToolFormState,
  type ParameterFormRow,
} from './businessToolFormUtils';
import { ExamplesTagInput, PlaceholderHelper, SectionHeading } from './businessToolFormControls';

export type BusinessToolBasicParamsProps = Readonly<{
  form: BusinessToolFormState;
  normalizedName: string;
  availablePlaceholders: string[];
  unresolvedPlaceholders: string[];
  onFormChange: (patch: Partial<BusinessToolFormState>) => void;
  onUpdateParameter: (id: string, patch: Partial<ParameterFormRow>) => void;
  onSetParameters: (parameters: ParameterFormRow[]) => void;
}>;

export function BusinessToolBasicAndParamsSections({
  form,
  normalizedName,
  availablePlaceholders,
  unresolvedPlaceholders,
  onFormChange,
  onUpdateParameter,
  onSetParameters,
}: BusinessToolBasicParamsProps) {
  return (
    <>
      <div className="flex flex-col gap-3">
        <SectionHeading title="Basic Information" />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:items-start">
          <div className="flex flex-col gap-1">
            <Input
              label="Tool Name"
              labelPlacement="outside"
              isRequired
              variant="bordered"
              placeholder="e.g. get_customer_info"
              value={form.name}
              onValueChange={(value) => onFormChange({ name: value })}
              description="Used as function name — no spaces."
            />
            {form.name.trim() && normalizedName !== form.name.trim() ? (
              <p className="text-xs text-default-500 font-mono">Final name: {normalizedName}</p>
            ) : null}
          </div>
          <Input
            label="Engaging Words"
            labelPlacement="outside"
            variant="bordered"
            placeholder="Processing your request..."
            value={form.engaging_words}
            maxLength={80}
            onValueChange={(value) => onFormChange({ engaging_words: value })}
            description="Spoken to the caller the moment the tool starts."
          />
          <Select
            label="Execution Mode"
            labelPlacement="outside"
            variant="bordered"
            selectedKeys={[form.execution_mode]}
            onSelectionChange={(keys) => {
              const value = Array.from(keys)[0] as
                | BusinessToolFormState['execution_mode']
                | undefined;
              if (!value) return;
              onFormChange({
                execution_mode: value,
                ...(value === 'immediate' ? { deferred_ack_message: '' } : {}),
              });
            }}
          >
            {BUSINESS_TOOL_EXECUTION_MODES.map((mode) => (
              <SelectItem key={mode} className="capitalize">
                {mode.charAt(0).toUpperCase() + mode.slice(1)}
              </SelectItem>
            ))}
          </Select>
          {form.execution_mode === 'deferred' ? (
            <Textarea
              label="Acknowledgement message"
              labelPlacement="outside"
              variant="bordered"
              minRows={2}
              placeholder="Message spoken while the deferred tool runs"
              value={form.deferred_ack_message}
              onValueChange={(value) => onFormChange({ deferred_ack_message: value })}
            />
          ) : null}
        </div>
        <Textarea
          label="Description"
          labelPlacement="outside"
          isRequired
          variant="bordered"
          minRows={3}
          placeholder="Describe what this tool does so the AI knows when to use it"
          value={form.description}
          onValueChange={(value) => onFormChange({ description: value })}
          description="The AI decides when to call this tool from this text. Include when to call it and any ordering (e.g. 'Call AFTER tool X succeeds')."
        />
      </div>

      <div className="flex flex-col gap-3">
        <SectionHeading
          title="Parameters"
          description="Fields the AI collects from the user to send in the API call."
          action={
            <CtyButton
              size="sm"
              variant="flat"
              startContent={<Icon icon="mdi:plus" width={14} height={14} />}
              onPress={() => onSetParameters([...form.parameters, createEmptyParameter()])}
            >
              Add Parameter
            </CtyButton>
          }
        />
        <div className="flex flex-col gap-3">
          {form.parameters.map((param) => (
            <div
              key={param.id}
              className="rounded-lg border border-divider bg-default-50 p-3 flex flex-col gap-3"
            >
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-12 sm:items-end">
                <Input
                  className="sm:col-span-3"
                  size="sm"
                  label="Name"
                  labelPlacement="outside"
                  variant="bordered"
                  placeholder="param_name"
                  value={param.name}
                  onValueChange={(value) =>
                    onUpdateParameter(param.id, {
                      name: value.replace(/[^a-zA-Z0-9_]/g, ''),
                    })
                  }
                />
                <Select
                  className="sm:col-span-2"
                  size="sm"
                  label="Type"
                  labelPlacement="outside"
                  variant="bordered"
                  selectedKeys={[param.type]}
                  onSelectionChange={(keys) => {
                    const value = Array.from(keys)[0] as ParameterFormRow['type'] | undefined;
                    if (value) onUpdateParameter(param.id, { type: value });
                  }}
                >
                  {BUSINESS_TOOL_PARAMETER_TYPES.map((type) => (
                    <SelectItem key={type}>{type}</SelectItem>
                  ))}
                </Select>
                <Input
                  className="sm:col-span-4"
                  size="sm"
                  label="Description"
                  labelPlacement="outside"
                  variant="bordered"
                  placeholder="Clear description for the AI"
                  value={param.description}
                  onValueChange={(value) => onUpdateParameter(param.id, { description: value })}
                />
                <div className="sm:col-span-3 flex items-center justify-between gap-2 min-h-8 pb-0.5">
                  <Switch
                    size="sm"
                    isSelected={param.required}
                    onValueChange={(value) => onUpdateParameter(param.id, { required: value })}
                  >
                    Required
                  </Switch>
                  <CtyButton
                    isIconOnly
                    size="sm"
                    variant="light"
                    color="danger"
                    aria-label="Remove parameter"
                    onPress={() =>
                      onSetParameters(form.parameters.filter((item) => item.id !== param.id))
                    }
                  >
                    <Icon icon="mdi:close" width={16} height={16} />
                  </CtyButton>
                </div>
              </div>
              <p className="text-xs text-default-400 -mt-1">
                Tells the AI what to collect from the caller and in what format.
              </p>
              <div>
                <p className="text-xs text-default-500 mb-1">Examples (optional)</p>
                <ExamplesTagInput
                  values={param.examples ?? []}
                  onChange={(examples) => onUpdateParameter(param.id, { examples })}
                  placeholder="Type example and press Enter"
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-lg border border-divider bg-content1 p-3 flex flex-col gap-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-default-500">
          Available placeholders
        </p>
        <PlaceholderHelper placeholders={availablePlaceholders} />
        {unresolvedPlaceholders.length > 0 ? (
          <p className="text-xs text-warning">
            Unresolved placeholders:{' '}
            {unresolvedPlaceholders.map((p) => `{{${p}}}`).join(', ')}
          </p>
        ) : null}
      </div>
    </>
  );
}
